/**
 * Tap a charted object to read its detail, in plain English. Lights get the
 * fuller treatment: the standard chart character (e.g. Fl(2).R.6s10M) plus a
 * readable expansion, colour, period, range, height, and each sector's bearing
 * range, colour and range where it's a sectored light. Missing attributes are
 * omitted rather than shown blank. No raw S-57 codes are surfaced.
 */
import { useChartPlotterStore } from '@/store';
import { trainingTideCovers } from '@/chart-sources';
import type { ChartFeature } from '@/types';
import { featurePoint } from './featureLabel';
import {
  formatPosition,
  formatDistance,
  formatBearing,
  rhumbDistanceNm,
  rhumbBearingDeg,
} from '@/nav';
import {
  objClassLabel,
  colourLabel,
  categoryLabel,
  shapeLabel,
  catlmkLabel,
  isLighthouse,
  lightReading,
  sectorReading,
  catobsLabel,
  watlevLabel,
  natsurLabel,
  zocAccuracyText,
  verticalClearanceM,
  clearanceNowM,
} from '@/chart';
import { useShellStore } from '@/store/shell';
import { useRoutingStore, nextWaypointId } from '@/store/routing';
import { useNotesStore, nextNoteId } from '@/store/notes';
import { setDestinationAndOfferRoute } from './setDestinationFlow';
import { useEnvStore } from '@/store/environment';
import { useSettingsStore } from '@/store/settings';
import { useUnitsStore, formatDepth } from '@/store/units';
import { haptic } from '@/ui/haptics';
import { getHeightPort, heightAt } from '@/tides/heights';
import { usePilotageStore, nextClearingId, nextHeadingId, nextClearingDepthId } from '@/store/pilotage';
import { useContourHighlightStore } from '@/store/contourHighlight';
import { useSafetyStore } from '@/store/safety';
import {
  clearingSide,
  headingDefaultFrom,
  type ClearingBearing,
  type HeadingBearing,
  type ClearingDepth,
} from '@/pilotage';
import { Sheet } from './Sheet';

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="acp-kv">
      <span className="k">{k}</span>
      <span className="v">{v}</span>
    </div>
  );
}

function deg(d: number): string {
  return `${Number.isInteger(d) ? d : d.toFixed(1)}°`;
}

function rawOf(f: ChartFeature): Record<string, unknown> {
  return (f.properties.attrs.raw ?? {}) as Record<string, unknown>;
}


function LightDetail({ lights }: { lights: ChartFeature[] }) {
  const depthUnit = useUnitsStore((s) => s.depth);
  const first = lights[0];
  const fattrs = first.properties.attrs;
  const fraw = rawOf(first);
  const sectors = lights
    .map((l) => sectorReading(rawOf(l), l.properties.attrs.colour))
    .filter((s): s is NonNullable<typeof s> => s !== null);
  const height = lights.map((l) => l.properties.attrs.heightM).find((h) => h != null);

  // For a sectored light the colour and range vary per sector, so keep them out
  // of the headline character and list them below.
  const reading = sectors.length
    ? lightReading({ ...fraw, VALNMR: undefined }, undefined)
    : lightReading(fraw, fattrs.colour, fattrs.lightCharacter as string | undefined);
  const single = !sectors.length;

  return (
    <>
      {reading.abbrev && <Row k="Light character" v={reading.abbrev} />}
      {reading.plain && (
        <p className="sub" style={{ marginTop: 2 }}>
          {reading.plain}
        </p>
      )}
      {single && colourLabel(fattrs.colour) && <Row k="Colour" v={colourLabel(fattrs.colour)!} />}
      {single && fattrs.rangeNm != null && <Row k="Range" v={`${fattrs.rangeNm} nm`} />}
      {height != null && <Row k="Height" v={formatDepth(Number(height), depthUnit)} />}
      {sectors.length > 0 && (
        <>
          <div className="acp-kv">
            <span className="k">
              <b>Sectors</b>
            </span>
            <span className="v">{sectors.length}</span>
          </div>
          {sectors.map((s, i) => (
            <Row
              key={i}
              k={`${deg(s.fromDeg)}–${deg(s.toDeg)}`}
              v={[s.colour, s.rangeNm != null ? `${s.rangeNm} nm` : null].filter(Boolean).join(', ')}
            />
          ))}
        </>
      )}
    </>
  );
}

function GenericDetail({ attrs }: { attrs: Record<string, unknown> }) {
  const depthUnit = useUnitsStore((s) => s.depth);
  const rows: [string, string | null][] = [
    ['Category', categoryLabel(attrs.category)],
    ['Shape', shapeLabel(attrs.shape)],
    ['Colour', colourLabel(attrs.colour)],
    ['Height', attrs.heightM != null ? formatDepth(Number(attrs.heightM), depthUnit) : null],
    ['Range', attrs.rangeNm != null ? `${attrs.rangeNm} nm` : null],
    [
      'Depth',
      attrs.depthMinM != null || attrs.depthMaxM != null
        ? `${attrs.depthMinM != null ? formatDepth(Number(attrs.depthMinM), depthUnit) : '?'}–${attrs.depthMaxM != null ? formatDepth(Number(attrs.depthMaxM), depthUnit) : '?'}`
        : attrs.depthM != null
          ? formatDepth(Number(attrs.depthM), depthUnit)
          : null,
    ],
    ['Sounding', attrs.soundingM != null ? formatDepth(Number(attrs.soundingM), depthUnit) : null],
  ];
  return (
    <>
      {rows.map(([k, v]) => (v ? <Row key={k} k={k} v={v} /> : null))}
    </>
  );
}

function num(v: unknown): number | null {
  const n = typeof v === 'number' ? v : parseFloat(String(v));
  return Number.isFinite(n) ? n : null;
}

/** Title for an obstruction or underwater rock, e.g. "Foul ground", "Rock (awash)". */
function obstructionTitle(objClass: string, raw: Record<string, unknown>): string {
  if (objClass === 'UWTROC') {
    const wl = watlevLabel(raw.WATLEV, true);
    return wl ? `Rock (${wl})` : 'Underwater rock';
  }
  if (objClass === 'WRECKS') {
    const wl = watlevLabel(raw.WATLEV, true);
    return wl ? `Wreck (${wl})` : 'Wreck';
  }
  return catobsLabel(raw.CATOBS) ?? 'Obstruction';
}

function ObstructionDetail({
  objClass,
  attrs,
  raw,
}: {
  objClass: string;
  attrs: Record<string, unknown>;
  raw: Record<string, unknown>;
}) {
  const depthUnit = useUnitsStore((s) => s.depth);
  const watlev = watlevLabel(raw.WATLEV);
  const nature = natsurLabel(raw.NATSUR);
  const depth = num(raw.VALSOU) ?? (attrs.depthM != null ? Number(attrs.depthM) : null);
  const category = objClass === 'OBSTRN' ? catobsLabel(raw.CATOBS) : null;
  return (
    <>
      {category && <Row k="Type" v={category} />}
      {watlev && <Row k="Water level" v={watlev} />}
      {depth != null && <Row k="Least depth over it" v={formatDepth(depth, depthUnit)} />}
      {nature && <Row k="Nature" v={nature} />}
    </>
  );
}

function BridgeDetail({ raw }: { raw: Record<string, unknown> }) {
  // Height of tide now at the chosen reference port + scrubber moment. Hooks run
  // before the early return so the rules of hooks hold.
  const streamTimeMs = useEnvStore((s) => s.streamTimeMs);
  const heightPortId = useEnvStore((s) => s.heightPortId);
  const chartProviderId = useChartPlotterStore((s) => s.chartProviderId);
  const airDraughtM = useSettingsStore((s) => s.airDraughtM);
  const depthUnit = useUnitsStore((s) => s.depth);
  const chartedM = verticalClearanceM(raw);
  const hPort = getHeightPort(heightPortId);
  // Same guard as the depth alarm: the training ports are fictional, so on a real
  // chart they contribute nothing and we fall back to the charted (datum)
  // clearance rather than adding an invented height of tide to a real bridge.
  const tidalHeightM = trainingTideCovers(chartProviderId)
    ? heightAt(hPort, streamTimeMs)
    : null;
  const nowM = clearanceNowM(chartedM, tidalHeightM);
  if (chartedM == null) return null;
  // What we compare our air draught against: the tide-corrected clearance when we
  // have a height of tide, otherwise the charted (datum) figure.
  const availM = nowM ?? chartedM;
  const margin = airDraughtM != null ? availM - airDraughtM : null;
  const fits = margin != null && margin >= 0;
  return (
    <>
      <Row k="Clearance (charted)" v={formatDepth(chartedM, depthUnit)} />
      <p className="sub" style={{ marginTop: 2 }}>
        Charted vertical clearance above datum, the fixed figure on the chart.
      </p>
      {nowM != null && tidalHeightM != null ? (
        <>
          <Row k="Clearance now" v={formatDepth(nowM, depthUnit)} />
          <p className="sub" style={{ marginTop: 2 }}>
            Charted clearance minus the height of tide now ({formatDepth(tidalHeightM, depthUnit)} at{' '}
            {hPort.name}). It drops as the tide rises. Set the moment with the time scrubber.
          </p>
        </>
      ) : (
        <p className="sub" style={{ marginTop: 2 }}>Not adjusted for the height of tide.</p>
      )}
      {margin != null ? (
        <>
          <Row
            k={fits ? 'We fit under' : 'We do NOT fit'}
            v={`${formatDepth(Math.abs(margin), depthUnit)} ${fits ? 'to spare' : 'short'}`}
          />
          <p className="sub" style={{ marginTop: 2, color: fits ? undefined : '#c0392b' }}>
            Our air draught {airDraughtM != null ? formatDepth(airDraughtM, depthUnit) : '—'} against the {nowM != null ? 'clearance now' : 'charted clearance'} (
            {formatDepth(availM, depthUnit)}).{' '}
            {fits
              ? 'Mind the margin as the tide makes — the clearance now falls as the tide rises.'
              : 'Do not pass at this state of tide.'}
          </p>
        </>
      ) : (
        <p className="sub" style={{ marginTop: 2 }}>
          Set our air draught in Settings to check whether we fit under.
        </p>
      )}
    </>
  );
}

export function FeatureSheet() {
  const feature = useChartPlotterStore((s) => s.selectedFeature);
  const siblings = useChartPlotterStore((s) => s.selectedSiblings);
  const ownship = useChartPlotterStore((s) => s.ownship.position);
  const setMode = useShellStore((s) => s.setMode);
  const openSheet = useShellStore((s) => s.openSheet);
  const showToast = useShellStore((s) => s.showToast);
  const addClearing = usePilotageStore((s) => s.addClearing);
  const addHeadingBearing = usePilotageStore((s) => s.addHeadingBearing);
  const addClearingDepth = usePilotageStore((s) => s.addClearingDepth);
  const armTransitPick = useShellStore((s) => s.armTransitPick);
  const addMark = useRoutingStore((s) => s.addMark);
  const addNote = useNotesStore((s) => s.addNote);
  const setEditingNote = useShellStore((s) => s.setEditingNote);
  const safetyDepthM = useSettingsStore((s) => s.safetyDepthM);
  const depthUnit = useUnitsStore((s) => s.depth);
  const toggleContourHighlight = useContourHighlightStore((s) => s.toggle);
  const highlightedContours = useContourHighlightStore((s) => s.values);
  const setSafetySettings = useSafetyStore((s) => s.setSettings);

  const p = feature?.properties;
  const attrs = (p?.attrs ?? {}) as Record<string, unknown>;
  const raw = (attrs.raw ?? {}) as Record<string, unknown>;
  const lights = siblings.filter((f) => f.properties.objClass === 'LIGHTS');
  const lighthouse = p?.objClass === 'LNDMRK' && isLighthouse(raw.CATLMK);

  const oc = p?.objClass;
  const contourDepthM = oc === 'DEPCNT' ? num(attrs.depthM) : null;
  const contourHighlighted = contourDepthM != null && highlightedContours.includes(contourDepthM);
  const obstruction = oc === 'OBSTRN' || oc === 'UWTROC' || oc === 'WRECKS';
  const leastDepthM = obstruction
    ? (num(raw.VALSOU) ?? (attrs.depthM != null ? Number(attrs.depthM) : null))
    : null;
  const isHarbour = oc === 'HRBFAC' || oc === 'ACHARE';
  const name = attrs.name as string | undefined;
  const typeLabel = lighthouse
    ? 'Lighthouse'
    : obstruction && oc
      ? obstructionTitle(oc, raw)
      : objClassLabel(oc);
  const title = feature ? name || typeLabel : 'Charted object';

  // Useful subtitle: the object's charted position, plus its range and bearing
  // from own ship when we have a fix.
  const pos = featurePoint(feature?.geometry);
  let sub: string | undefined;
  if (pos) {
    sub = formatPosition(pos);
    if (ownship) {
      const rng = rhumbDistanceNm(ownship, pos);
      const brg = rhumbBearingDeg(ownship, pos);
      sub += ` · ${formatDistance(rng)}, ${formatBearing(brg)} from boat`;
    }
  }

  // Anchor a clearing bearing on this object. Defaults the bearing to the
  // object's current bearing from the boat (the hand-bearing reading) and puts
  // the safe side on the side the boat is on now; both are adjustable after.
  function setClearingBearingHere() {
    if (!pos) return;
    const bearingDeg = ownship ? rhumbBearingDeg(ownship, pos) : 0;
    const lengthNm = ownship ? Math.max(0.1, Math.min(rhumbDistanceNm(ownship, pos), 3)) : 0.5;
    const cb: ClearingBearing = {
      id: nextClearingId(),
      anchor: pos,
      anchorLabel: title,
      bearingDeg,
      lengthNm,
      safeSide: 'left',
      alarm: true,
      planId: null,
    };
    if (ownship) {
      const side = clearingSide(cb, ownship);
      if (side !== 'on') cb.safeSide = side;
    }
    addClearing(cb);
    setMode('Plotting');
    openSheet('clearing');
    showToast('Clearing bearing set — drag the free end to adjust');
  }

  // Start a transit on this object: arm the second-object pick, then the next
  // tap on a charted object plots the transit between the two.
  // Start a transit with this object as the BACK (rear) mark; the next tap
  // places the FRONT mark (snapping to a nearby object, or free placement).
  function plotTransitHere() {
    if (!pos) return;
    armTransitPick({ position: pos, label: title });
    setMode('Plotting');
    showToast(`Back mark: ${title} — now tap the front mark`);
  }

  // A heading bearing (object ahead, steer toward it) or back bearing (object
  // astern, the reciprocal you'd read looking back). Anchored on this object,
  // with the vessel end at own ship (or a short default line we can drag).
  function setHeadingBearingHere(kind: 'heading' | 'back') {
    if (!pos) return;
    const hb: HeadingBearing = {
      id: nextHeadingId(),
      kind,
      anchor: pos,
      anchorLabel: title,
      from: ownship ?? headingDefaultFrom(pos),
      planId: null,
    };
    addHeadingBearing(hb);
    setMode('Plotting');
    openSheet('heading');
    showToast(
      kind === 'back'
        ? 'Back bearing set — drag the vessel end to adjust'
        : 'Heading bearing set — drag the vessel end to adjust',
    );
  }

  // Set this depth contour as a do-not-cross clearing depth, pinned to its
  // charted value. The alarm guards it by default; tune it in the editor.
  function setClearingDepthHere() {
    if (!pos || contourDepthM == null) return;
    const cd: ClearingDepth = {
      id: nextClearingDepthId(),
      valueM: contourDepthM,
      anchor: pos,
      alarm: true,
      planId: null,
    };
    addClearingDepth(cd);
    // Also make this the grounding safety contour, so the depth we're calling a
    // do-not-cross is the same one the grounding check treats as the shallow
    // boundary. That's the "set as clearing/safety contour" the reports asked
    // for: the guarded clearing depth and the safety contour move together.
    setSafetySettings({ depthM: contourDepthM });
    setMode('Plotting');
    openSheet('clearingdepth');
    showToast(`Clearing contour set at ${contourDepthM} m — guarding, and now your safety contour`);
  }

  // Emphasise (or un-emphasise) this depth contour on the chart. Purely visual,
  // no alarm, so it's the light-touch "just make it stand out" option.
  function toggleHighlightHere() {
    if (contourDepthM == null) return;
    const wasOn = highlightedContours.includes(contourDepthM);
    toggleContourHighlight(contourDepthM);
    showToast(
      wasOn
        ? `Highlight removed from the ${contourDepthM} m contour`
        : `Highlighting the ${contourDepthM} m contour`,
    );
  }

  // Drop a standalone waypoint on this object's charted position.
  function addWaypointHere() {
    if (!pos) return;
    addMark({ id: nextWaypointId(), position: pos, name: title, icon: 'plot-waypoint' });
    haptic('commit');
    showToast('Waypoint added');
  }

  // Drop a note on this object's charted position and open its editor. We
  // already have the location (the object we're looking at), so this skips the
  // tap-to-place step and goes straight to writing the note.
  function addNoteHere() {
    if (!pos) return;
    const id = nextNoteId();
    const now = Date.now();
    addNote({ id, position: pos, title: title || undefined, text: '', createdMs: now, updatedMs: now });
    haptic('commit');
    setEditingNote(id);
    openSheet('note');
  }

  // Make this harbour/anchorage the passage destination.
  function setDestinationHere() {
    if (!pos) return;
    // Set it, then immediately offer to plot a route to it (quick confirm).
    haptic('commit');
    setDestinationAndOfferRoute(pos, title);
  }

  return (
    <Sheet name="feature" title={title} sub={sub}>
      {feature && p && (
        <>
          {name && <Row k="Type" v={typeLabel} />}
          {obstruction && oc ? (
            <ObstructionDetail objClass={oc} attrs={attrs} raw={raw} />
          ) : oc === 'BRIDGE' ? (
            <BridgeDetail raw={raw} />
          ) : lights.length > 0 ? (
            <LightDetail lights={lights} />
          ) : (
            <GenericDetail attrs={attrs} />
          )}
          {leastDepthM != null && (
            <>
              <Row k="Least depth over it" v={formatDepth(leastDepthM, depthUnit)} />
              <p
                className="sub"
                style={{ marginTop: 2, color: leastDepthM <= safetyDepthM ? '#c0392b' : undefined }}
              >
                {leastDepthM <= safetyDepthM
                  ? `Shoaler than your ${formatDepth(safetyDepthM, depthUnit)} safety depth — treat as a hazard.`
                  : `Deeper than your ${formatDepth(safetyDepthM, depthUnit)} safety depth.`}
              </p>
            </>
          )}
          {p.objClass === 'LNDMRK' && !lighthouse && catlmkLabel(raw.CATLMK) && (
            <Row k="Landmark" v={catlmkLabel(raw.CATLMK)!} />
          )}
          {lights.length === 0 && lighthouse && attrs.heightM != null && (
            <Row k="Height" v={formatDepth(Number(attrs.heightM), depthUnit)} />
          )}
          {zocAccuracyText(p.zoc) && (
            <p className="sub" style={{ marginTop: 10 }}>
              {zocAccuracyText(p.zoc)}
            </p>
          )}
          {p.sorind && <Row k="Source" v={String(p.sorind)} />}
          {p.sordat && <Row k="Source date" v={String(p.sordat)} />}
          {Array.isArray(p.notes) &&
            p.notes.length > 0 &&
            p.notes.map((n: string, i: number) => (
              <p key={i} className="sub" style={{ marginTop: 10 }}>
                {n}
              </p>
            ))}
          {pos && isHarbour && (
            <div className="acp-btn-row">
              <button type="button" className="acp-btn primary full" onClick={setDestinationHere}>
                Set as destination
              </button>
            </div>
          )}
          {pos && contourDepthM != null && (
            <>
              <div className="acp-btn-row">
                <button type="button" className="acp-btn full" onClick={toggleHighlightHere}>
                  {contourHighlighted ? 'Remove highlight' : 'Highlight this contour'}
                </button>
              </div>
              <div className="acp-btn-row">
                <button type="button" className="acp-btn primary full" onClick={setClearingDepthHere}>
                  Set as clearing contour ({contourDepthM} m)
                </button>
              </div>
            </>
          )}
          {pos && contourDepthM == null && (
            <>
              <div className="acp-btn-row">
                <button type="button" className="acp-btn primary full" onClick={setClearingBearingHere}>
                  Set clearing bearing
                </button>
              </div>
              <div className="acp-btn-row">
                <button type="button" className="acp-btn full" onClick={plotTransitHere}>
                  Plot transit
                </button>
              </div>
              <div className="acp-btn-row">
                <button
                  type="button"
                  className="acp-btn full"
                  onClick={() => setHeadingBearingHere('heading')}
                >
                  Set heading/back bearing
                </button>
              </div>
            </>
          )}
          {pos && (
            <div className="acp-btn-row">
              <button type="button" className="acp-btn full" onClick={addWaypointHere}>
                Add waypoint here
              </button>
            </div>
          )}
          {pos && (
            <div className="acp-btn-row">
              <button type="button" className="acp-btn full" onClick={addNoteHere}>
                Add note here
              </button>
            </div>
          )}
        </>
      )}
    </Sheet>
  );
}
